import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";

type CompanyLegalForm = Database["public"]["Enums"]["company_legal_form"];

export interface Company {
  id: string;
  user_id: string;
  name: string;
  legal_form: CompanyLegalForm;
  siren: string | null;
  siret: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  activity_description: string | null;
  created_at: string;
  updated_at: string;
}

export type CompanyInput = Omit<Company, "id" | "user_id" | "created_at" | "updated_at">;

export function useCompanies() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all companies
  const { data: companies = [], isLoading, error } = useQuery({
    queryKey: ["companies", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .eq("user_id", user.id)
        .order("name", { ascending: true });

      if (error) throw error;
      return data as Company[];
    },
    enabled: !!user?.id,
  });

  // Create company
  const createCompany = useMutation({
    mutationFn: async (input: CompanyInput) => {
      if (!user?.id) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("companies")
        .insert({
          user_id: user.id,
          name: input.name,
          legal_form: input.legal_form,
          siren: input.siren,
          siret: input.siret,
          address: input.address,
          city: input.city,
          postal_code: input.postal_code,
          activity_description: input.activity_description,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      toast.success("Entreprise ajoutée");
    },
    onError: (error: Error) => {
      console.error("Create company error:", error);
      toast.error("Erreur lors de l'ajout");
    },
  });

  // Update company
  const updateCompany = useMutation({
    mutationFn: async ({ id, ...input }: Partial<CompanyInput> & { id: string }) => {
      if (!user?.id) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("companies")
        .update({
          name: input.name,
          legal_form: input.legal_form,
          siren: input.siren,
          siret: input.siret,
          address: input.address,
          city: input.city,
          postal_code: input.postal_code,
          activity_description: input.activity_description,
        })
        .eq("id", id)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      toast.success("Entreprise mise à jour");
    },
    onError: (error: Error) => {
      console.error("Update company error:", error);
      toast.error("Erreur lors de la mise à jour");
    },
  });

  // Delete company
  const deleteCompany = useMutation({
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error("Non authentifié");

      // First delete related legal documents
      await supabase
        .from("legal_documents")
        .delete()
        .eq("company_id", id);

      const { error } = await supabase
        .from("companies")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["legal-documents"] });
      toast.success("Entreprise supprimée");
    },
    onError: (error: Error) => {
      console.error("Delete company error:", error);
      toast.error("Erreur lors de la suppression");
    },
  });

  return {
    companies,
    isLoading,
    error,
    createCompany,
    updateCompany,
    deleteCompany,
  };
}

// Legal form labels in French
export const LEGAL_FORM_LABELS: Record<CompanyLegalForm, string> = {
  auto_entrepreneur: "Auto-entrepreneur",
  eurl: "EURL",
  sarl: "SARL",
  sas: "SAS",
  sasu: "SASU",
  sci: "SCI",
  association: "Association",
  other: "Autre",
};

// Legal form icons
export const LEGAL_FORM_ICONS: Record<CompanyLegalForm, string> = {
  auto_entrepreneur: "👤",
  eurl: "🏢",
  sarl: "🏢",
  sas: "🏛️",
  sasu: "🏛️",
  sci: "🏠",
  association: "🤝",
  other: "📋",
};
